/**
 * DocumentToolbar — ЄДИНИЙ тулбар для editor'ів ДОКУМЕНТІВ (Закон дизайну #6).
 *
 * ч.639 (рішення власника 2026-09-01) — ДВОРІВНЕВА схема:
 *  - ДІЇ документа живуть на НИЖНІЙ СТАТИЧНІЙ ПЛАШЦІ (.docbar, portal у body,
 *    завжди на виду при скролі): назад → до списку → зберегти →
 *    провести(/розпровести) → видалити → друк → «ще дії ▾» (extraActions).
 *  - На МІСЦІ тулбара у шапці редактора лишаються ІНФО-кнопки:
 *    дт-кт · історія · звʼязані документи.
 *
 * Текст-only, БЕЗ іконок/емодзі (Закон дизайну #7).
 * Presentational: НЕ містить mutation/api — editor володіє логікою і передає
 * колбеки + флаги. «назад» — history back (вкладки документів = звичайна
 * SPA-навігація, тому navigate(-1) повертає на попередню сторінку).
 *
 * Контекст-правила (ч.702, рішення власника: ВСІ кнопки видно ОДРАЗУ —
 * недоступні до збереження/проведення НЕ ховаються, а деактивуються з
 * підказкою):
 *  - isNew: видалити/друк показані disabled («Спершу збережіть документ»);
 *    провести = save+post одним кліком.
 *  - видалити на проведеному — disabled («спершу розпровести»).
 *  - Ховаємо лише НЕЗАСТОСОВНЕ до типу документа: postable=false — без
 *    провести/розпровести; deletable=false — без видалити; onPrint не
 *    заданий (нема друкованої форми) — без друку.
 *  - extraActions → кнопка «ще дії» з випадаючим ВГОРУ списком; дії,
 *    недоступні за СТАНОМ, сторінка передає з disabled+title (не ховає).
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOpenTabs } from '@/store/openTabs';
import { READ_ONLY_HINT, useAccessMode } from '@/store/accessMode';

import { DtKtButton, type DtKtDocRef } from './DtKtJournal';
import { DocHistoryButton } from './DocHistory';
import { RelatedDocsButton } from './RelatedDocs';
import { RecordOwnerInline } from './RecordOwner';
import DocStatusBadge, { type StatusBadge } from './DocStatusBadge';
import { useQuery } from '@tanstack/react-query';
import { fetchUnpostedNotes } from '@/shared/utils/unpostedNotes';

export interface ExtraAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  danger?: boolean;
}

interface DocumentToolbarProps {
  /** Кнопка «До списку» (editor: () => navigate(listPath)). */
  onBackToList: () => void;

  /** «Зберегти». */
  onSave: () => void;
  saveDisabled?: boolean;
  /** Підказка на кнопці «зберегти» (напр. чому вона неактивна). */
  saveTitle?: string;
  savePending?: boolean;
  /** Текст кнопки збереження (дефолт «зберегти»; напр. «згенерувати»). */
  saveLabel?: string;
  /** savable=false → кнопку «зберегти» сховано (док без ручного збереження). */
  savable?: boolean;

  /** Проведення. postable=false → Провести/Розпровести сховані. */
  postable?: boolean;
  isPosted?: boolean;
  onPost?: () => void;
  onUnpost?: () => void;
  postDisabled?: boolean;
  postPending?: boolean;
  unpostPending?: boolean;
  /** ч.1120: «розпровести» недоступне з причиною (напр. період під забороною змін). */
  unpostDisabled?: boolean;
  unpostTitle?: string;
  postTitle?: string;
  postLabel?: string;

  /** «Видалити». deletable=false → кнопка схована. confirm — у editor'і. */
  deletable?: boolean;
  onDelete?: () => void;
  deleteDisabled?: boolean;
  deletePending?: boolean;
  deleteTitle?: string;

  /** «друк» — рендериться ЛИШЕ якщо заданий. Назву форми НЕ пишемо. */
  onPrint?: () => void;
  /** ч.761: кілька друкованих форм — випадаючий список «друк ▾» (замість onPrint). */
  printOptions?: ExtraAction[];
  printDisabled?: boolean;

  /** new-документ: видалити/друк disabled, провести = save+post. */
  isNew?: boolean;

  /** «дт-кт»/«історія»/«звʼязані» — інфо-рядок у шапці редактора. */
  // Довідники (контрагент, організація) дт-кт/історію/звʼязані НЕ показують (ч.742) — не передавати.
  entriesDoc?: DtKtDocRef;
  /** ч.809: лише «історія» (аудит-журнал) без дт-кт/звʼязаних — для довідників
   *  з аудит-тригером (працівник, фізособа). */
  historyDoc?: DtKtDocRef;
  /** ч.1313: запис, відповідального якого показати в рядку статусу (довідник без
   *  історії — картка контрагента). Документ береться з entriesDoc / historyDoc сам. */
  ownerDoc?: DtKtDocRef;

  /** Додаткові дії сторінки → кнопка «ще дії ▾» на плашці. */
  extraActions?: ExtraAction[];

  /** Додаткові бейджі стану ПІСЛЯ авто-бейджа проведення
   *  (напр. «закрито»/«відкрито», «в процесі»). */
  extraStatusBadges?: StatusBadge[];
  /** Сховати авто-бейдж «проведено/не проведено» (де він не має сенсу). */
  hidePostedBadge?: boolean;

  /** ч.849 (новий дизайн): історія/звʼязані живуть у правій колонці
   *  (DocRail), у рядку статусу лишається лише «дт-кт»; «ще дії» теж
   *  віддані рейці — на плашці кнопки «ще дії ▾» не показуємо. */
  railMode?: boolean;
}

/** Нижня плашка: portal у body + вирівнювання лівого краю по .app__main. */
export function DocActionBar({ children }: { children: React.ReactNode }) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const measure = () => {
      const main = document.querySelector('.app__main');
      setLeft(main ? Math.round(main.getBoundingClientRect().left) : 0);
    };
    measure();
    window.addEventListener('resize', measure);
    // Сайдбар згортається без resize вікна — спостерігаємо за layout'ом.
    // Guard: у jsdom (vitest) ResizeObserver відсутній.
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(measure) : null;
    if (ro) {
      // САМЕ .app__main: згортання лівого субменю міняє його ширину/left
      // без resize вікна — RO на main ловить і це (зауваження власника ч.640).
      const main = document.querySelector('.app__main');
      if (main) ro.observe(main);
      const layout = document.querySelector('.app');
      if (layout) ro.observe(layout);
    }
    document.body.classList.add('has-docbar');
    return () => {
      window.removeEventListener('resize', measure);
      ro?.disconnect();
      document.body.classList.remove('has-docbar');
    };
  }, []);
  return createPortal(
    <div className="docbar" style={{ ['--docbar-left' as never]: `${left}px` }}>
      {children}
    </div>,
    document.body,
  );
}

function MoreActions({ actions, label = 'ще дії ▾' }: { actions: ExtraAction[]; label?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  if (actions.length === 0) return null;
  return (
    <div className="docbar__more" ref={ref}>
      <button onClick={() => setOpen((v) => !v)}>{label}</button>
      {open && (
        <div className="docbar__more-menu">
          {actions.map((a, i) => (
            <button key={i} disabled={a.disabled} title={a.title}
              style={a.danger ? { color: '#dc2626' } : undefined}
              onClick={() => { setOpen(false); a.onClick(); }}>
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Бейдж непроведеного документа (ч.1218). Якщо документ перенесено з 1С і він не
 *  проводиться ЗА ЗАДУМОМ (рахунок закрито при імпорті, проводки приїхали довідкою,
 *  відпустку перенесено «як є») — так і пишемо, з поясненням у підказці; інакше звичайне
 *  «не проведено». Правило — на сервері (/documents/unposted-notes). */
function UnpostedBadge({ doc }: { doc: DtKtDocRef }) {
  const q = useQuery({
    queryKey: ['unposted-note', doc.type, doc.id],
    queryFn: () => fetchUnpostedNotes(doc.type, [doc.id!]),
    enabled: !!doc.id && doc.id !== 'new',
    staleTime: 60_000,
  });
  const note = doc.id ? q.data?.[doc.id] : undefined;
  return note
    ? <DocStatusBadge label={note.label ?? 'з 1С · не проводиться'} tone="blue" title={note.text} />
    : <DocStatusBadge label="не проведено" tone="gray" />;
}

export default function DocumentToolbar({
  onBackToList,
  onSave, saveDisabled, saveTitle, savePending, saveLabel, savable = true,
  postable = true, isPosted = false, onPost, onUnpost,
  postDisabled, postPending, unpostPending, unpostDisabled, unpostTitle, postTitle, postLabel,
  deletable = true, onDelete, deleteDisabled, deletePending, deleteTitle,
  onPrint, printOptions,
  printDisabled,
  isNew = false,
  entriesDoc,
  historyDoc,
  ownerDoc,
  extraActions,
  extraStatusBadges,
  hidePostedBadge,
  railMode = false,
}: DocumentToolbarProps) {
  const navigate = useNavigate();
  // ч.950: закриття вкладки видаленого документа (див. onClick кнопки «видалити»).
  const { pathname } = useLocation();
  const closeTab = useOpenTabs((st) => st.close);
  const closeTabForCurrentDoc = () => closeTab(pathname);
  // ч.1119: роль «лише перегляд» — дії документа вимкнені з підказкою (сервер
  // усе одно відмовить; вимкнена кнопка чесніша за тост після натискання).
  const readOnly = useAccessMode((st) => st.readOnly);
  // Тексти кнопок — лише з малої літери (Закон дизайну #7).
  return (
    <>
      {/* Інфо-рядок у РЯДКУ НАЗВИ сторінки (ч.641): бейджі стану
          (проведено/не проведено + додаткові) · дт-кт · історія · звʼязані. */}
      <div className="docinfo" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        {postable && !isNew && !hidePostedBadge && (
          isPosted || !(entriesDoc ?? historyDoc)?.id
            ? <DocStatusBadge label={isPosted ? 'проведено' : 'не проведено'} tone={isPosted ? 'green' : 'gray'} />
            : <UnpostedBadge doc={(entriesDoc ?? historyDoc)!} />
        )}
        {(extraStatusBadges ?? []).map((b, i) => <DocStatusBadge key={i} {...b} />)}
        {entriesDoc && <DtKtButton doc={entriesDoc} />}
        {/* ч.849: у railMode історія/звʼязані — у правій колонці. */}
        {entriesDoc && !railMode && <DocHistoryButton doc={entriesDoc} />}
        {entriesDoc && !railMode && <RelatedDocsButton doc={entriesDoc} />}
        {!entriesDoc && historyDoc && !railMode && <DocHistoryButton doc={historyDoc} />}
        {/* ч.1313: відповідальний (у railMode — карткою в правій колонці). */}
        {!isNew && !railMode && (ownerDoc ?? entriesDoc ?? historyDoc)?.id
          && <RecordOwnerInline doc={ownerDoc ?? entriesDoc ?? historyDoc} />}
      </div>

      <DocActionBar>
        <button onClick={() => navigate(-1)}
          title="Повернутись на попередню сторінку">назад</button>
        <button onClick={onBackToList}>до списку</button>

        {savable && (
          <button
            className="primary"
            onClick={onSave}
            disabled={saveDisabled || savePending || readOnly}
            title={readOnly ? READ_ONLY_HINT : saveTitle}
          >
            {saveLabel ?? 'зберегти'}
          </button>
        )}

        {/* провести / розпровести. На new — лише провести (save+post одним кліком). */}
        {postable && (
          isPosted && !isNew ? (
            <button onClick={onUnpost} disabled={unpostPending || unpostDisabled || readOnly}
              title={readOnly ? READ_ONLY_HINT : unpostTitle}>
              розпровести
            </button>
          ) : !isPosted ? (
            <button
              className="primary"
              onClick={onPost}
              disabled={postDisabled || postPending || readOnly}
              title={readOnly ? READ_ONLY_HINT : postTitle}
            >
              {postLabel ?? 'провести'}
            </button>
          ) : null
        )}

        {/* видалити — видно завжди (ч.702); активна лише для збереженого
            DRAFT, на new/проведеному — disabled з підказкою. */}
        {deletable && onDelete && (
          <button
            className="danger"
            /* ч.950 (баг власника): видалений документ лишався вкладкою «у
               кеші» — на неї можна було перейти, хоч у списку документа вже
               немає. Закриваємо вкладку цього документа разом з видаленням. */
            onClick={() => { closeTabForCurrentDoc(); onDelete(); }}
            disabled={deleteDisabled || deletePending || isNew || isPosted || readOnly}
            title={readOnly
              ? READ_ONLY_HINT
              : isNew
              ? 'Спершу збережіть документ'
              : isPosted
                ? 'Проведений документ не видаляється — спершу розпровести'
                : deleteTitle}
          >
            видалити
          </button>
        )}

        {/* друк — де є друкована форма; активна після збереження. */}
        {printOptions && printOptions.length > 0 && (
          isNew
            ? <button disabled title="Спершу збережіть документ">друк ▾</button>
            : <MoreActions actions={printOptions} label="друк ▾" />
        )}
        {onPrint && !printOptions && (
          <button onClick={onPrint} disabled={printDisabled ?? isNew}
            title={isNew ? 'Спершу збережіть документ' : undefined}>
            друк
          </button>
        )}

        {/* ч.849: у railMode «ще дії» живуть у правій колонці (Повʼязані дії). */}
        {!railMode && <MoreActions actions={extraActions ?? []} />}
      </DocActionBar>
    </>
  );
}
